// DOM drag ghost in #fx (IMPLEMENTATION §2.8 step 3, UX §0 z5, §3.9): ONE pooled node, transform-only,
// opacity 1, centred on the drop hotspot (liftPx above the finger) so it passes over the DOM chrome (the
// counter). Its icon is drawn into its one canvas by an IconProvider (M6: the catalog frame through the
// placeholder drawer; M8: art/domIcons). Snap-back: `snapBack(x, y, ms)` animates to the origin with Back.out (juice.json overshoot)
// on requestAnimationFrame, then hides; 0 ms (?test: zero-duration tweens) hides at once.
import { backOut } from './ease.ts';
import type { IconKey, IconProvider } from './icons.ts';

export interface DragGhost {
  show(key: IconKey, sizePx: number): void;
  /** Centre of the ghost = the drop hotspot, in client CSS px. */
  moveTo(cx: number, cy: number): void;
  snapBack(x: number, y: number, ms: number): void;
  hide(): void;
  visible(): boolean;
  el: HTMLElement;
}

export interface DragGhostOptions {
  icons: IconProvider;
  /** juice.json easing.popOvershoot (Back.out). */
  overshoot: number;
  now(): number;
  raf(cb: (t: number) => void): number;
  cancelRaf(id: number): void;
}

export function createDragGhost(fxRoot: HTMLElement, o: DragGhostOptions): DragGhost {
  const doc = fxRoot.ownerDocument;
  const el = doc.createElement('div');
  el.className = 'gm-drag-ghost';
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText =
    'position:absolute;left:0;top:0;display:none;box-sizing:border-box;align-items:center;' +
    'justify-content:center;will-change:transform;opacity:1;pointer-events:none;' +
    'filter:drop-shadow(0 6px 8px rgba(0,0,0,.45));';
  const canvas = doc.createElement('canvas');
  canvas.style.cssText = 'width:100%;height:100%;display:block;';
  el.appendChild(canvas);
  fxRoot.appendChild(el);
  let size = 0;
  let shown = false;
  let cx = 0;
  let cy = 0;
  let anim = 0;

  const place = (x: number, y: number): void => {
    cx = x;
    cy = y;
    el.style.transform = `translate3d(${x - size / 2}px,${y - size / 2}px,0)`;
  };
  const stop = (): void => {
    if (anim !== 0) o.cancelRaf(anim);
    anim = 0;
  };
  const hide = (): void => {
    stop();
    el.style.display = 'none';
    shown = false;
  };

  return {
    el,
    show(key, sizePx) {
      stop();
      size = sizePx;
      el.style.width = `${sizePx}px`;
      el.style.height = `${sizePx}px`;
      // straight into the ghost's own pooled canvas (no scratch canvas per drag: §2.9 iOS canvas memory)
      o.icons.draw(key, canvas, sizePx);
      el.style.display = 'flex';
      shown = true;
    },
    moveTo(x, y) {
      stop();
      place(x, y);
    },
    snapBack(x, y, ms) {
      stop();
      if (!shown || !(ms > 0)) {
        hide();
        return;
      }
      const x0 = cx;
      const y0 = cy;
      const t0 = o.now();
      const step = (): void => {
        const t = (o.now() - t0) / ms;
        if (t >= 1) {
          hide();
          return;
        }
        const k = backOut(t, o.overshoot);
        place(x0 + (x - x0) * k, y0 + (y - y0) * k);
        anim = o.raf(step);
      };
      anim = o.raf(step);
    },
    hide,
    visible: () => shown,
  };
}
